<template>
  <div>
    <AppLoader v-if="isLoading" class="text-center" small />
    <b-container fluid>
      <b-navbar toggleable="lg" type="dark" variant="light">
        <b-navbar-brand href="#" class="text-dark">
          <strong>PRI Control Groups</strong>
        </b-navbar-brand>

        <b-navbar-toggle target="nav-collapse"></b-navbar-toggle>

        <b-collapse id="nav-collapse" is-nav>
          <!-- Right aligned nav items -->
          <b-navbar-nav class="ml-auto">
            <b-nav-form>
              <b-button
                variant="outline-primary"
                size="sm"
                @click="showModal = !showModal"
                class="mx-2"
              >
                <font-awesome-icon icon="plus" />
              </b-button>

              <b-button variant="outline-info" size="sm" @click="reloadTable">
                <font-awesome-icon icon="sync" />
              </b-button>
              <b-form-group
                label-cols-sm="3"
                label-align-sm="right"
                label-size="sm"
                label-for="filterInput"
              >
                <b-input-group size="sm">
                  <b-form-input
                    v-model="filter"
                    type="search"
                    id="filterInput"
                    placeholder="Type to Search"
                  ></b-form-input>
                  <b-input-group-append>
                    <b-button :disabled="!filter" @click="filter = ''">Clear</b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </b-nav-form>
          </b-navbar-nav>
        </b-collapse>
      </b-navbar>

      <b-table
        class="mx-1"
        bordered
        sticky-header="calc(100vh - 200px)"
        ref="table"
        id="my-table"
        :fixed="true"
        :busy.sync="isLoading"
        :items="getControlGroupTableData"
        :fields="tableFields"
        :filter="filter"
        :per-page="perPage"
        :current-page="currentPage"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        responsive="sm"
      >
        <template v-slot:cell(actions)="data">
          <b-button
            size="sm"
            class="mr-2"
            type="button"
            variant="outline-info"
            @click="onEditControlGroup(data)"
          >
            <font-awesome-icon v-b-tooltip.hover title="Edit Field" icon="edit" />
          </b-button>
          <b-button
            size="sm"
            type="button"
            variant="outline-danger"
            @click="onDeleteControlGroupModal(data)"
          >
            <font-awesome-icon v-b-tooltip.hover title="Delete Field" icon="trash" />
          </b-button>
        </template>
      </b-table>
      <b-row>
        <b-col cols="2">
          <b-form-select v-model="perPage" id="perPageSelect" size="sm" :options="pageOptions"></b-form-select>
        </b-col>
        <b-col>
          <b-pagination
            class="float-right"
            v-model="currentPage"
            :total-rows="rows"
            :per-page="perPage"
            first-text="First"
            prev-text="Prev"
            next-text="Next"
            last-text="Last"
            aria-controls="my-table"
          ></b-pagination>
        </b-col>
      </b-row>
    </b-container>
    <b-modal
      no-close-on-backdrop
      no-close-on-esc
      hide-header-close
      size="lg"
      :title="editControlGroup ? 'Update Pri Control Groups' : 'Create Pri Control Groups'"
      v-model="showModal"
    >
      <AppLoader v-if="isLoadingPri" class="text-center" small />
      <div>
        <div v-if="!submiting && !responseSuccess">
          <b-row>
            <b-col cols="6">
              <label>Select Pri</label>
              <v-select
                :searchable="true"
                :clearable="true"
                label="text"
                code="value"
                v-model="$v.form.selectedPri.$model"
                :state="$v.form.selectedPri.$dirty ? !$v.form.selectedPri.$error : null"
                :class="$v.form.selectedPri.$error ? 'border border-danger rounded' : null"
                aria-describedby
                @input="setSelectPri"
                :options="priOptions"
                placeholder="Select Any PRI"
              ></v-select>
            </b-col>
            <b-col cols="6">
              <b-form-group label="Title:">
                <b-form-input
                  v-model="$v.form.titleInput.$model"
                  :state="$v.form.titleInput.$dirty ? !$v.form.titleInput.$error : null"
                  placeholder="Enter Title"
                ></b-form-input>
              </b-form-group>
            </b-col>
          </b-row>
          <b-form-group label="Description:">
            <b-form-textarea
              v-model="form.descriptionInput"
              rows="2"
              max-rows="2"
              placeholder="Enter Description"
            ></b-form-textarea>
          </b-form-group>
        </div>
        <div v-else-if="submiting && !responseSuccess">
          <p>{{editControlGroup ? 'Your Control Group is being Updating......' : 'Your Control Group is being Creating......'}}</p>
          <b-spinner type="grow" label="Loading..."></b-spinner>
        </div>
        <div v-else>
          <p class="my-2 text-center">
            {{ editControlGroup ? 'Your Control Group has been updated successfully.' : 'Your Control Group has been added successfully.' }}
            <br />
          </p>
        </div>
      </div>
      <template v-slot:modal-footer>
        <b-button v-if="!submiting" variant="outline-danger" @click="resetModal">Close</b-button>
        <b-button
          v-if="!submiting && !responseSuccess"
          variant="outline-success"
          @click="checkValidation()"
        >{{ editControlGroup? 'Update' : 'Save' }}</b-button>
      </template>
    </b-modal>
    <b-modal
      no-close-on-backdrop
      no-close-on-esc
      hide-header-close
      title="Delete Field"
      v-model="showDeleteControlGroupModal"
    >
      <div>
        <div v-if="!submiting && !responseSuccess">
          <p>Are you sure you want to delete this Control Group ?</p>
        </div>
        <div v-else-if="submiting && !responseSuccess">
          <p>Control Group is being deleting......</p>
          <b-spinner type="grow" label="Loading..."></b-spinner>
        </div>
        <div v-else>
          <p class="my-2 text-center">
            Control Group has been deleted successfully.
            <br />
          </p>
        </div>
      </div>
      <template v-slot:modal-footer>
        <b-button v-if="!submiting" @click="resetDeleteModal">Close</b-button>
        <b-button
          v-if="!submiting && !responseSuccess"
          variant="danger"
          @click="deleteControlGroup()"
        >
          <font-awesome-icon
            v-b-tooltip.hover
            title="Delete Field"
            class="mr-1 text-light"
            icon="trash"
          />Yes
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { HTTP } from "../../utils/HTTP";
import AppLoader from "../../components/Loader/Loader";
import { validationMixin } from "vuelidate";
import { required } from "vuelidate/lib/validators";

export default {
  name: "PriControlGroups",
  mixins: [validationMixin],
  components: {
    AppLoader
  },
  data() {
    return {
      form: {
        descriptionInput: null,
        titleInput: null,
        selectedPri: null
      },
      showDeleteControlGroupModal: false,
      isLoadingPri: false,
      selectPriId: null,
      priOptions: [],
      showModal: false,
      isLoading: false,
      tableFields: [
        { key: "created_at", label: "Age", sortable: true },
        { key: "control_group_title", label: "Title", sortable: true },
        { key: "description", label: "Description", sortable: true },
        { key: "title", label: "PRI Catgeories", sortable: true },
        { key: "actions", label: "Actions", sortable: false }
      ],
      filter: null,
      perPage: 10,
      totalRows: 0,
      currentPage: 1,
      sortBy: "id",
      sortDesc: true,
      pageOptions: [5, 10, 25, 50, 75, 100],
      submiting: false,
      responseSuccess: false,
      editControlGroup: false,
      controlGroupId: null
    };
  },
  computed: {
    rows() {
      return this.totalRows;
    }
  },
  validations: {
    form: {
      selectedPri: { required },
      titleInput: { required }
    }
  },
  mounted() {
    this.getPriList();
  },
  methods: {
    reloadTable() {
      this.$root.$emit("bv::refresh::table", "my-table");
    },
    async getControlGroupTableData() {
      let formData = {
        filter: this.filter,
        sort: this.sortBy + "|" + (this.sortDesc == true ? "desc" : "asc"),
        per_page: this.perPage,
        page: this.currentPage,
        company_id: this.$route.params.id
      };

      let token = "Bearer " + localStorage.getItem("accessToken");
      this.isLoading = true;
      let result = HTTP(
        "post",
        "v1/control-group/get-control-group-list",
        formData,
        {
          Authorization: token
        }
      );

      return result
        .then(response => {
          const items = response.data.data;
          this.isLoading = false;
          this.totalRows = response.data.total;
          return items;
        })
        .catch(error => {
          this.isLoading = false;
          return [error];
        });
    },
    setSelectPri(value) {
      // // console.log(value);
      this.selectPriId = null;
      this.selectPriId = value.value;
    },
    async getPriList() {
      this.priOptions = [];
      this.isLoading = true;
      let token = "Bearer " + localStorage.getItem("accessToken");
      try {
        let result = await HTTP(
          "get",
          "v1/control-group/getprioptions/" + this.$route.params.id,
          null,
          {
            Authorization: token
          }
        );
        this.priOptions = result.data.pri;
        this.isLoading = false;
      } catch (error) {
        this.isLoading = false;
      }
    },
    resetModal() {
      this.form.selectedPri = null;
      this.form.descriptionInput = null;
      this.form.titleInput = null;
      this.selectPriId = null;
      this.submiting = false;
      this.isLoading = false;
      this.responseSuccess = false;
      this.editControlGroup = false;
      this.$v.$reset();
      this.showModal = false;
    },
    checkValidation() {
      this.$v.form.$touch();
      if (this.$v.form.$anyError) {
        return;
      } else if (this.editControlGroup) {
        this.updateControlGroup().then(this.reloadTable);
      } else {
        this.saveControlGroup().then(this.reloadTable);
      }
    },
    async saveControlGroup() {
      let formData = {
        title: this.form.titleInput,
        description: this.form.descriptionInput,
        client_pri_versions_id: this.selectPriId,
        company_id: this.$route.params.id
      };
      try {
        let token = "Bearer " + localStorage.getItem("accessToken");
        this.submiting = true;
        this.isLoading = true;
        await HTTP(
          "post",
          "v1/control-group/create-control-group",
          formData,
          {
            Authorization: token
          }
        );
        this.submiting = false;
        this.isLoading = false;
        this.responseSuccess = true;
        this.form.selectedPri = null;
        this.form.descriptionInput = null;
        this.form.titleInput = null;
      } catch (err) {
        return [err];
        // // console.log("------------erroror", err);
      }
    },
    onEditControlGroup(data) {
      this.controlGroupId = data.item.id;
      this.getPriValue(data.item.client_pri_versions_id);
      this.editControlGroup = true;
      this.form.descriptionInput = data.item.description
        ? data.item.description
        : null;
      this.form.titleInput = data.item.control_group_title;
      this.showModal = true;
    },
    async getPriValue(priId) {
      this.isLoadingPri = true;
      let token = "Bearer " + localStorage.getItem("accessToken");
      try {
        let result = await HTTP(
          "get",
          "v1/control-group/getprivalue/" + priId,
          null,
          {
            Authorization: token
          }
        );
        this.form.selectedPri = result.data.pri;
        this.selectPriId = result.data.pri.value;
        this.isLoadingPri = false;
      } catch (error) {
        this.isLoadingPri = false;
      }
    },
    async updateControlGroup() {
      let formData = {
        control_group_id: this.controlGroupId,
        title: this.form.titleInput,
        description: this.form.descriptionInput,
        client_pri_versions_id: this.selectPriId,
        company_id: this.$route.params.id
      };
      try {
        let token = "Bearer " + localStorage.getItem("accessToken");
        this.submiting = true;
        this.isLoading = true;
        await HTTP(
          "post",
          "v1/control-group/update-control-group",
          formData,
          {
            Authorization: token
          }
        );
        this.submiting = false;
        this.isLoading = false;
        this.responseSuccess = true;
        this.form.selectedPri = null;
        this.form.descriptionInput = null;
        this.form.titleInput = null;
        this.controlGroupId = null;
      } catch (err) {
        return [err];
        // // console.log("------------erroror", err);
      }
    },
    onDeleteControlGroupModal(data) {
      this.controlGroupId = data.item.id;
      this.showDeleteControlGroupModal = true;
    },
    async deleteControlGroup() {
      this.isLoading = true;
      try {
        let token = "Bearer " + localStorage.getItem("accessToken");
        this.submiting = true;
        this.isLoading = true;
        await HTTP(
          "get",
          "v1/control-group/deletecontrolgroup/" + this.controlGroupId,
          null,
          {
            Authorization: token
          }
        );
        this.submiting = false;
        this.isLoading = false;
        this.responseSuccess = true;
      } catch (error) {
        this.isLoading = false;
      }
    },
    resetDeleteModal() {
      this.reloadTable();
      this.submiting = false;
      this.isLoading = false;
      this.responseSuccess = false;
      this.showDeleteControlGroupModal = false;
    }
  }
};
</script>

<style scoped>
</style>
